import {
  Palette,
  Sparkles,
  Type,
  Zap,
  MonitorSmartphone,
  type LucideIcon,
} from 'lucide-react';
import type {
  DesignSystem,
  UxPatterns,
  MobileOs,
  WebsiteUxPatterns,
} from './components/DesignSystemPanel';

type SelectOption<T extends string> = { value: T; label: string };

export type DesignSystemSectionId = 'brand' | 'colors' | 'typography' | 'visual-style' | 'patterns';

export type DesignSystemSection = {
  id: DesignSystemSectionId;
  label: string;
  icon: LucideIcon;
};

export const DESIGN_SYSTEM_SECTIONS: DesignSystemSection[] = [
  { id: 'brand', label: 'Brand', icon: Sparkles },
  { id: 'colors', label: 'Colors', icon: Palette },
  { id: 'typography', label: 'Typography', icon: Type },
  { id: 'visual-style', label: 'Visual style', icon: Zap },
  { id: 'patterns', label: 'Platform & UX', icon: MonitorSmartphone },
];

export type ColorPalette = {
  id: string;
  name: string;
  swatches: [string, string, string];
  primary: string;
  accent: string;
  primaryForeground: string;
  secondaryForeground: string;
};

export const COLOR_PALETTES: ColorPalette[] = [
  {
    id: 'ideoz-slate',
    name: 'Ideoz slate',
    swatches: ['#1E293B', '#334155', '#64748B'],
    primary: '#334155',
    accent: '#64748B',
    primaryForeground: '#FFFFFF',
    secondaryForeground: '#FFFFFF',
  },
  {
    id: 'signal-indigo',
    name: 'Signal Indigo',
    swatches: ['#312E81', '#4338CA', '#818CF8'],
    primary: '#4338CA',
    accent: '#818CF8',
    primaryForeground: '#FFFFFF',
    secondaryForeground: '#1E1B4B',
  },
  {
    id: 'field-emerald',
    name: 'Field emerald',
    swatches: ['#064E3B', '#0F7D5C', '#33C48D'],
    primary: '#0F7D5C',
    accent: '#33C48D',
    primaryForeground: '#FFFFFF',
    secondaryForeground: '#064E3B',
  },
  {
    id: 'studio-amber',
    name: 'Studio amber',
    swatches: ['#78350F', '#B45309', '#FBBF24'],
    primary: '#B45309',
    accent: '#FBBF24',
    primaryForeground: '#FFFFFF',
    secondaryForeground: '#78350F',
  },
];

export type FontPairing = {
  id: string;
  label: string;
  body: string;
  heading?: string;
};

export const FONT_PAIRINGS: FontPairing[] = [
  { id: 'exo-2', label: 'Exo 2', body: 'Exo 2' },
  { id: 'lora-inter', label: 'Lora / Inter', body: 'Inter', heading: 'Lora' },
  {
    id: 'ibm-plex',
    label: 'IBM Plex Mono / IBM Plex Sans',
    body: 'IBM Plex Sans',
    heading: 'IBM Plex Mono',
  },
  {
    id: 'ibm-plex-arabic',
    label: 'IBM Plex Sans Arabic',
    body: 'IBM Plex Sans Arabic',
    heading: 'IBM Plex Sans Arabic',
  },
];

/** Arabic-script Google Fonts — shown in a dedicated picker section. */
export const ARABIC_FONTS = [
  'IBM Plex Sans Arabic',
  'Noto Sans Arabic',
  'Noto Kufi Arabic',
  'Cairo',
  'Tajawal',
  'Almarai',
  'Amiri',
  'Readex Pro',
  'El Messiri',
  'Markazi Text',
];

/** Curated popular fonts — shown first and used as fallback if metadata fetch fails. */
export const POPULAR_FONTS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Nunito',
  'Work Sans',
  'Playfair Display',
  'Source Sans Pro',
  'Ubuntu',
  'PT Sans',
  'Merriweather',
  'Oswald',
  'Rubik',
  'DM Sans',
  'Space Grotesk',
  'Manrope',
  'Outfit',
  'IBM Plex Sans',
  'IBM Plex Mono',
  'Noto Sans',
  'Exo 2',
  'Quicksand',
  'Mulish',
  'Karla',
  'Barlow',
  'Fira Sans',
  'Archivo',
  'Lexend',
  'Red Hat Display',
  'Epilogue',
  'Sora',
  'Instrument Sans',
  'Lora',
  'Geist',
  'Geist Mono',
];

export type PillOption<T extends string> = { value: T; label: string };

export const CORNER_RADIUS_PILLS: PillOption<NonNullable<DesignSystem['borderRadius']>>[] = [
  { value: 'none', label: 'Sharp' },
  { value: 'md', label: 'Soft' },
  { value: 'lg', label: 'Round' },
  { value: 'full', label: 'Pill' },
];

export const ELEVATION_PILLS: PillOption<NonNullable<DesignSystem['elevationStyle']>>[] = [
  { value: 'flat', label: 'Flat' },
  { value: 'soft-shadow', label: 'Soft' },
  { value: 'material', label: 'Lifted' },
];

/**
 * The design-system page exposes elevation style, not the legacy `shadows`
 * scale — so `shadows` never received a real value and stayed `none` in the
 * saved DTO. Derive it from the chosen elevation so the persisted `shadows`
 * intensity stays consistent with the elevation the generator should apply.
 */
export function elevationToShadows(
  elevationStyle?: DesignSystem['elevationStyle']
): NonNullable<DesignSystem['shadows']> {
  switch (elevationStyle) {
    case 'flat':
      return 'none';
    case 'soft-shadow':
      return 'small';
    case 'glass':
    case 'neumorphism':
      return 'medium';
    case 'material':
      return 'large';
    default:
      return 'none';
  }
}

export const THEME_OPTIONS: PillOption<NonNullable<DesignSystem['theme']>>[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export const THEME_COLOR_PRESETS: Record<
  NonNullable<DesignSystem['theme']>,
  Pick<DesignSystem, 'appBg' | 'surfaceColor' | 'borderColor' | 'mutedTextColor'>
> = {
  light: {
    appBg: '#FFFFFF',
    surfaceColor: '#F8FAFC',
    borderColor: '#E2E8F0',
    mutedTextColor: '#64748B',
  },
  dark: {
    appBg: '#111827',
    surfaceColor: '#1F2937',
    borderColor: '#374151',
    mutedTextColor: '#9CA3AF',
  },
};

export const DENSITY_OPTIONS: Array<SelectOption<NonNullable<DesignSystem['density']>>> = [
  { value: 'compact', label: 'Compact' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'spacious', label: 'Spacious' },
];

export const FORM_PATTERN_OPTIONS: Array<SelectOption<NonNullable<UxPatterns['addPattern']>>> = [
  { value: 'modal', label: 'Modal dialog' },
  { value: 'drawer-right', label: 'Side drawer' },
  { value: 'drawer-bottom', label: 'Bottom drawer' },
  { value: 'new-page', label: 'Dedicated page' },
  { value: 'inline-row', label: 'Inline row' },
];

export const DETAIL_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['detailPattern']>>
> = [
  { value: 'page', label: 'Dedicated page' },
  { value: 'side-panel', label: 'Side panel' },
  { value: 'modal', label: 'Modal' },
  { value: 'expand-row', label: 'Expand row' },
];

export const DELETE_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['deletePattern']>>
> = [
  { value: 'confirm-modal', label: 'Confirm modal' },
  { value: 'undo-toast', label: 'Undo toast' },
  { value: 'swipe', label: 'Swipe to delete' },
];

export const NAV_PATTERN_OPTIONS: Array<SelectOption<NonNullable<UxPatterns['navPattern']>>> = [
  { value: 'top-bar', label: 'Top bar' },
  { value: 'side-nav', label: 'Side navigation' },
  { value: 'bottom-tabs', label: 'Bottom tabs' },
  { value: 'drawer', label: 'Drawer / hamburger' },
  { value: 'rail', label: 'Navigation rail' },
];

export const LIST_PATTERN_OPTIONS: Array<SelectOption<NonNullable<UxPatterns['listPattern']>>> = [
  { value: 'table', label: 'Table (rows + headers)' },
  { value: 'cards', label: 'Card grid' },
  { value: 'list-rows', label: 'List rows' },
  { value: 'kanban', label: 'Kanban board' },
  { value: 'feed', label: 'Feed' },
];

export const CONTAINER_WIDTH_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['containerWidth']>>
> = [
  { value: 'narrow-centered', label: 'Narrow centered' },
  { value: 'wide', label: 'Wide' },
  { value: 'full-bleed', label: 'Full bleed' },
];

export const SEARCH_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['searchPattern']>>
> = [
  { value: 'top-bar-inline', label: 'Top-bar inline' },
  { value: 'global-modal', label: 'Global modal / palette' },
  { value: 'page-header', label: 'Page header' },
  { value: 'none', label: 'No search' },
];

export const FILTER_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['filterPattern']>>
> = [
  { value: 'top-bar', label: 'Top bar' },
  { value: 'side-panel', label: 'Side panel' },
  { value: 'modal', label: 'Modal' },
  { value: 'inline-dropdowns', label: 'Inline dropdowns' },
];

export const LOADING_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['loadingPattern']>>
> = [
  { value: 'spinner', label: 'Spinner' },
  { value: 'skeleton', label: 'Skeleton' },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'progress-bar', label: 'Progress bar' },
];

export const EMPTY_STATE_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['emptyStatePattern']>>
> = [
  { value: 'illustration-cta', label: 'Illustration + CTA' },
  { value: 'minimal-text', label: 'Minimal text' },
  { value: 'helper-guidance', label: 'Helper guidance' },
];

export function getActivePaletteId(designSystem: DesignSystem): string | null {
  const match = COLOR_PALETTES.find(
    (p) => p.primary.toLowerCase() === designSystem.primaryColor?.toLowerCase(),
  );
  return match?.id ?? null;
}

export function getActiveFontPairingId(designSystem: DesignSystem): string | null {
  const match = FONT_PAIRINGS.find(
    (p) =>
      p.body === designSystem.fontFamily &&
      (p.heading ?? p.body) === (designSystem.headingFontFamily ?? designSystem.fontFamily),
  );
  return match?.id ?? null;
}

export function borderRadiusToPx(radius?: DesignSystem['borderRadius']): string {
  switch (radius) {
    case 'none':
      return '2px';
    case 'sm':
      return '4px';
    case 'md':
      return '8px';
    case 'lg':
      return '12px';
    case 'full':
      return '9999px';
    default:
      return '8px';
  }
}

/**
 * Product type & language options for the Design System page. These persist to
 * the project row (via the DS endpoint's write-through) and drive the prototyper:
 * product type → prototype mode, language → language + text direction. Values
 * match what ideoz-ai-api `project_meta.py` understands (`mobile`, not
 * `mobile_app`, so it maps to the `mobile` prototyper mode).
 */
export const PRODUCT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'web_app', label: 'Web App' },
  { value: 'mobile', label: 'Mobile App' },
  { value: 'website', label: 'Website' },
];

export const LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'english', label: 'English' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'bilingual', label: 'Bilingual' },
];

// ---------------------------------------------------------------------------
// Platform experience — the active-platform toggle and per-platform UX sets.
// Brand, colors and type stay shared; only these UX layers differ per platform.
// ---------------------------------------------------------------------------

/** The active-platform toggle above the UX patterns section. */
export const PLATFORM_OPTIONS: Array<{ value: NonNullable<DesignSystem['activePlatform']>; label: string }> = [
  { value: 'web_app', label: 'Web app' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'website', label: 'Website' },
];

/** Mobile: pick a native experience; its conventions are then applied automatically. */
export const MOBILE_OS_OPTIONS: Array<{ value: MobileOs; label: string; description: string }> = [
  { value: 'ios', label: 'iOS experience', description: 'Follows Apple Human Interface Guidelines' },
  { value: 'android', label: 'Android experience', description: 'Follows Material Design 3' },
];

/**
 * Read-only conventions applied automatically for each mobile OS — shown as a
 * table under the iOS / Android chooser (not individually editable).
 */
export const MOBILE_CONVENTIONS: Record<MobileOs, Array<{ rowKey: string; label: string; value: string }>> = {
  ios: [
    { rowKey: 'navigation', label: 'Navigation', value: 'Bottom tab bar' },
    { rowKey: 'addItem', label: 'Add new item', value: 'Modal sheet' },
    { rowKey: 'editItem', label: 'Edit item', value: 'Modal sheet' },
    { rowKey: 'deleteConfirm', label: 'Delete confirmation', value: 'Action sheet' },
    { rowKey: 'listDisplay', label: 'List display', value: 'Inset grouped list' },
    { rowKey: 'search', label: 'Search', value: 'Pull-down search bar' },
    { rowKey: 'detailView', label: 'Detail view', value: 'Push navigation' },
    { rowKey: 'backNav', label: 'Back navigation', value: 'Edge swipe + back label' },
    { rowKey: 'typography', label: 'Typography', value: 'SF Pro' },
  ],
  android: [
    { rowKey: 'navigation', label: 'Navigation', value: 'Navigation bar' },
    { rowKey: 'addItem', label: 'Add new item', value: 'Floating action button' },
    { rowKey: 'editItem', label: 'Edit item', value: 'Full-screen dialog' },
    { rowKey: 'deleteConfirm', label: 'Delete confirmation', value: 'Undo snackbar' },
    { rowKey: 'listDisplay', label: 'List display', value: 'Card list' },
    { rowKey: 'search', label: 'Search', value: 'Search bar in top app bar' },
    { rowKey: 'detailView', label: 'Detail view', value: 'Full-screen transition' },
    { rowKey: 'backNav', label: 'Back navigation', value: 'System back gesture' },
    { rowKey: 'typography', label: 'Typography', value: 'Roboto' },
  ],
};

// Website UX pattern option sets (marketing-site behaviours).
export const WEBSITE_NAV_OPTIONS: Array<SelectOption<NonNullable<WebsiteUxPatterns['navPattern']>>> = [
  { value: 'top-bar', label: 'Top bar' },
  { value: 'minimal-header', label: 'Minimal header' },
  { value: 'sidebar', label: 'Sidebar' },
];

export const WEBSITE_CONTENT_LAYOUT_OPTIONS: Array<
  SelectOption<NonNullable<WebsiteUxPatterns['contentLayout']>>
> = [
  { value: 'sectioned', label: 'Sectioned page' },
  { value: 'long-scroll', label: 'Long scroll' },
  { value: 'multi-page', label: 'Multi-page' },
];

export const WEBSITE_PRIMARY_CTA_OPTIONS: Array<
  SelectOption<NonNullable<WebsiteUxPatterns['primaryCta']>>
> = [
  { value: 'header-hero', label: 'Header + hero' },
  { value: 'hero-only', label: 'Hero only' },
  { value: 'sticky-bar', label: 'Sticky bar' },
];

export const WEBSITE_HERO_OPTIONS: Array<SelectOption<NonNullable<WebsiteUxPatterns['heroStyle']>>> = [
  { value: 'centered', label: 'Centered statement' },
  { value: 'split-visual', label: 'Split with visual' },
  { value: 'video-bg', label: 'Video background' },
];

export const WEBSITE_LIST_DISPLAY_OPTIONS: Array<
  SelectOption<NonNullable<WebsiteUxPatterns['listDisplay']>>
> = [
  { value: 'card-grid', label: 'Card grid' },
  { value: 'feature-rows', label: 'Feature rows' },
  { value: 'compact-list', label: 'Compact list' },
];

export const WEBSITE_SEARCH_OPTIONS: Array<
  SelectOption<NonNullable<WebsiteUxPatterns['searchPattern']>>
> = [
  { value: 'header-field', label: 'Header field' },
  { value: 'none', label: 'None' },
  { value: 'dedicated-page', label: 'Dedicated page' },
];
